import React from 'react';
import { FormikProvider } from 'formik';
import type { StoryblokRichtext } from '../../../.storyblok/types/storyblok';
import PopUp from '../Modal/PopUp';
import PrivacyPolicyConfirmationPopup from '../PrivacyPolicy/PrivacyPolicyConfirmationPopup';
import FooterEmailForm from './FooterEmailForm';
import HeaderEmailForm from './HeaderEmailForm';
import StandaloneEmailForm from './StandaloneEmailForm';
import { useNewsletterSignup } from './useNewsletterSignup';

interface NewsletterSignupFormProps {
  /** Which presentation to render. */
  variant?: 'standalone' | 'header' | 'footer';
  label?: string;
  submitLabel?: string;
  privacyPolicyText?: StoryblokRichtext;
  /** `header` variant only: light input/icon treatment for dark navs. */
  navFontColorLight?: boolean;
}

/**
 * Newsletter signup form: owns the shared submit flow (consent gate, HubSpot POST,
 * success/failure popup) and renders the requested presentation.
 */
function NewsletterSignupForm({
  variant = 'standalone',
  label,
  submitLabel,
  privacyPolicyText,
  navFontColorLight = false,
}: NewsletterSignupFormProps) {
  const { formik, openPrivacyPolicyPopup, openLastStepPopup, message, onChoosingConsent, onCloseLastStepPopup } =
    useNewsletterSignup();

  return (
    <>
      <FormikProvider value={formik}>
        {variant === 'standalone' && (
          <StandaloneEmailForm
            onFormSubmit={formik.handleSubmit}
            isSubmitting={formik.isSubmitting}
            label={label}
            submitLabel={submitLabel}
            disclaimerText={privacyPolicyText}
          />
        )}

        {variant === 'header' && (
          <HeaderEmailForm onFormSubmit={formik.handleSubmit} navFontColorLight={navFontColorLight} />
        )}

        {variant === 'footer' && (
          <FooterEmailForm onFormSubmit={formik.handleSubmit} privacyPolicyText={privacyPolicyText} />
        )}
      </FormikProvider>

      {openPrivacyPolicyPopup && <PrivacyPolicyConfirmationPopup onPopUpClose={onChoosingConsent} />}

      {openLastStepPopup && (
        <PopUp
          onPopUpClose={onCloseLastStepPopup}
          isHeadless
          width={{
            desktopLg: '36.5vw',
            desktopSm: '40vw',
            tablet: '60vw',
            mobile: '90vw',
          }}
          padding={{ mobile: '20px' }}
        >
          <div className="mb-[30px] flex justify-center text-center">
            <p className="typography-eyebrow-s lg:typography-eyebrow-m !text-navy-600">{message}</p>
          </div>

          <div className="flex justify-center text-center">
            <button
              type="button"
              onClick={onCloseLastStepPopup}
              className="h-[35px] w-[149px] rounded-[20px] border-[0.75px] border-solid border-cerulean-400
                bg-cerulean-400 font-brown text-[12px] font-bold uppercase leading-[1.7] tracking-[2.16px]
                text-beige-100 hover:bg-cerulean-700 lg:h-[40px] lg:w-[220px] lg:text-[16px] lg:leading-[1.6]
                lg:tracking-[2.88px]"
            >
              Close
            </button>
          </div>
        </PopUp>
      )}
    </>
  );
}

export default NewsletterSignupForm;
